/**
 * UploadFlyout — Reusable upload button + flyout popover for media uploads.
 *
 * Extracted from ChatInput.tsx for reuse in ReferenceSelector and other contexts.
 * Encapsulates toggle state, refs, outside-click dismiss, hidden file inputs,
 * and the flyout popover JSX.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Images, Plus, Upload } from 'lucide-react'
import { createNadDebug } from '../../utils/debugLog'
import {
  MEDIA_ACCEPT_BY_TYPE,
  classifyMediaFiles,
  describeMediaFiles,
  type ClassifiedMediaFile,
} from '../../utils/uploadFileValidation'

export type UploadMediaType = 'image' | 'video' | 'audio'

const multiFileUploadLog = createNadDebug('dw:multi-file-upload')

const DEFAULT_ALLOWED_TYPES: readonly UploadMediaType[] = ['image', 'video', 'audio']

/**
 * Accepted document extensions.
 *
 * DUPLICATES the backend allowlist (`SUPPORTED_DOCUMENT_TYPES` /
 * `EXTENSION_MIME` in `services/document_text.py`) — this is a browser picker
 * hint, so it cannot be fetched at render time. Divergence is not silent in
 * either direction: an extension the backend refuses returns a specific 415
 * that the composer surfaces verbatim, and one missing here is still
 * selectable via "All files". Keep the two in step when PR2 adds formats.
 */
const DOCUMENT_ACCEPT = '.pdf,.docx,.pptx,.txt,.md,.markdown'

export interface UploadFlyoutProps {
  /** Per-type entries (Image / Video / Audio). Unused when `onUploadMedia` is set. */
  onUploadFile?: (file: File, type: UploadMediaType) => void
  /**
   * Opt-in document picker (RFC-1218 PR1). Separate from `onUploadFile`
   * because a document never becomes an asset or a node — it is parsed into
   * prompt context. Providing this is what surfaces the Document entry.
   */
  onUploadDocument?: (file: File) => void
  /**
   * Opt-in merged media picker. Providing this replaces the per-type
   * Image / Video / Audio entries with ONE "Upload media" entry whose input
   * is multi-select and accepts every allowed media family. The selection is
   * classified once (selection order preserved); `rejected` holds files that
   * are not image/video/audio and must be reported by the caller.
   */
  onUploadMedia?: (accepted: ClassifiedMediaFile[], rejected: File[]) => void
  allowedTypes?: readonly UploadMediaType[]
  disabled?: boolean
  size?: 'sm' | 'md'
  testIdPrefix?: string
  label?: string
  buttonClassName?: string
  onButtonMouseEnter?: () => void
  /** Called each time the flyout opens, with the number of options it shows. */
  onMenuOpen?: (optionCount: number) => void
  title?: string
  icon?: 'upload' | 'plus'
}

const UploadFlyout = memo(function UploadFlyout({
  onUploadFile,
  onUploadDocument,
  onUploadMedia,
  disabled = false,
  size = 'md',
  testIdPrefix = 'upload',
  label,
  buttonClassName,
  onButtonMouseEnter,
  onMenuOpen,
  title,
  icon = 'upload',
  allowedTypes = DEFAULT_ALLOWED_TYPES,
}: UploadFlyoutProps) {
  const { t } = useTranslation('shared')
  const resolvedTitle = title ?? t('uploadFlyout.uploadFile')
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)
  const mediaInputRef = useRef<HTMLInputElement>(null)
  const documentInputRef = useRef<HTMLInputElement>(null)
  const canUploadImage = allowedTypes.includes('image')
  const canUploadVideo = allowedTypes.includes('video')
  const canUploadAudio = allowedTypes.includes('audio')
  const mergedMedia = Boolean(onUploadMedia) && allowedTypes.length > 0
  const mergedMediaAccept = allowedTypes.map((type) => MEDIA_ACCEPT_BY_TYPE[type]).join(',')
  // RFC-1218 PR1: documents are prompt context, not canvas media, so they ride
  // their OWN callback rather than widening UploadMediaType. Widening it broke
  // every consumer that types its handler narrowly (the landing composers and
  // ReferenceSelector), and it would have implied a document can be dropped
  // into an upload node.
  const canUploadDocument = Boolean(onUploadDocument)
  // Document-only mode is legitimate: a caller can pass `allowedTypes={[]}`
  // with `onUploadDocument` and want just the Document entry. Gating solely on
  // `allowedTypes` would disable the trigger and make that option unreachable.
  const isDisabled = disabled || (allowedTypes.length === 0 && !canUploadDocument)

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target
      if (
        target instanceof globalThis.Node &&
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMenu])

  // Close the open menu when it becomes disabled OR when the allowed-type set
  // changes (e.g. the landing composer removes 'image' once the image cap is
  // reached — the image option/input vanish, so the open menu must re-anchor).
  // Consumers that pass a stable allowedTypes set never trigger this.
  const allowedTypesKey = allowedTypes.join(',')
  const prevAllowedTypesKeyRef = useRef(allowedTypesKey)
  useEffect(() => {
    if (isDisabled && showMenu) {
      setShowMenu(false)
    }
  }, [isDisabled, showMenu])
  useEffect(() => {
    if (prevAllowedTypesKeyRef.current !== allowedTypesKey) {
      prevAllowedTypesKeyRef.current = allowedTypesKey
      setShowMenu(false)
    }
  }, [allowedTypesKey])

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: UploadMediaType) => {
      const files = Array.from(e.target.files ?? [])
      if (files.length > 0) {
        onUploadFile?.(files[0], type)
      }
      // Reset the input so the same file can be selected again
      e.target.value = ''
      setShowMenu(false)
    },
    [onUploadFile],
  )

  const handleMediaSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      if (files.length > 0 && onUploadMedia) {
        const { accepted, rejected } = classifyMediaFiles(files)
        multiFileUploadLog({
          event: 'flyout-media-selected',
          surface: testIdPrefix,
          selected_count: files.length,
          accepted_count: accepted.length,
          accepted_files: describeMediaFiles(accepted),
          rejected_count: rejected.length,
          ...(rejected.length > 0 && { rejected_files: rejected.map((file) => file.name).join(',') }),
        })
        onUploadMedia(accepted, rejected)
      }
      e.target.value = ''
      setShowMenu(false)
    },
    [onUploadMedia, testIdPrefix],
  )

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'
  const buttonPadding = size === 'sm' ? 'p-1.5' : 'p-2'
  const Icon = icon === 'plus' ? Plus : Upload

  return (
    <div className="relative">
      {/* Hidden file inputs */}
      {mergedMedia && (
        <input
          ref={mediaInputRef}
          type="file"
          accept={mergedMediaAccept}
          multiple
          onChange={handleMediaSelect}
          className="hidden"
          data-testid={`${testIdPrefix}-media-input`}
        />
      )}
      {!mergedMedia && canUploadImage && (
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileSelect(e, 'image')}
          className="hidden"
          data-testid={`${testIdPrefix}-image-input`}
        />
      )}
      {!mergedMedia && canUploadVideo && (
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          onChange={(e) => handleFileSelect(e, 'video')}
          className="hidden"
          data-testid={`${testIdPrefix}-video-input`}
        />
      )}
      {!mergedMedia && canUploadAudio && (
        <input
          ref={audioInputRef}
          type="file"
          accept="audio/*"
          onChange={(e) => handleFileSelect(e, 'audio')}
          className="hidden"
          data-testid={`${testIdPrefix}-audio-input`}
        />
      )}

      {canUploadDocument && (
        <input
          ref={documentInputRef}
          type="file"
          accept={DOCUMENT_ACCEPT}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) onUploadDocument?.(file)
            e.target.value = ''
            setShowMenu(false)
          }}
          className="hidden"
          data-testid={`${testIdPrefix}-document-input`}
        />
      )}

      {/* Upload button */}
      <button
        ref={buttonRef}
        type="button"
        disabled={isDisabled}
        onClick={() => {
          if (!showMenu && onMenuOpen) {
            const mediaOptions = mergedMedia
              ? 1
              : [canUploadImage, canUploadVideo, canUploadAudio].filter(Boolean).length
            onMenuOpen(mediaOptions + (canUploadDocument ? 1 : 0))
          }
          setShowMenu((prev) => !prev)
        }}
        onMouseEnter={onButtonMouseEnter}
        className={buttonClassName || `${buttonPadding} rounded-md text-text-muted hover:text-text-secondary hover:bg-surface-2 transition-colors disabled:opacity-50`}
        title={resolvedTitle}
        data-testid={`${testIdPrefix}-button`}
      >
        <Icon className={iconSize} strokeWidth={1.5} />
        {label && <span>{label}</span>}
      </button>

      {/* Flyout popover */}
      {showMenu && (
        <div
          ref={menuRef}
          className="absolute bottom-full left-0 mb-2 bg-surface-1 border border-border-subtle rounded-lg shadow-lg z-50 min-w-[160px] py-1"
          data-testid={`${testIdPrefix}-menu`}
        >
          {mergedMedia && (
            <button
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary transition-colors"
              onClick={() => mediaInputRef.current?.click()}
              data-testid={`${testIdPrefix}-media-option`}
            >
              <Images className="w-4 h-4 text-text-muted" strokeWidth={1.5} />
              {t('uploadFlyout.uploadMedia')}
            </button>
          )}
          {!mergedMedia && canUploadImage && (
            <button
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary transition-colors"
              onClick={() => imageInputRef.current?.click()}
              data-testid={`${testIdPrefix}-image-option`}
            >
              <svg
                className="w-4 h-4 text-text-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              {t('uploadFlyout.uploadImage')}
            </button>
          )}
          {!mergedMedia && canUploadVideo && (
            <button
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary transition-colors"
              onClick={() => videoInputRef.current?.click()}
              data-testid={`${testIdPrefix}-video-option`}
            >
              <svg
                className="w-4 h-4 text-text-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              {t('uploadFlyout.uploadVideo')}
            </button>
          )}
          {!mergedMedia && canUploadAudio && (
            <button
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary transition-colors"
              onClick={() => audioInputRef.current?.click()}
              data-testid={`${testIdPrefix}-audio-option`}
            >
              <svg
                className="w-4 h-4 text-text-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
              {t('uploadFlyout.uploadAudio')}
            </button>
          )}
          {canUploadDocument && (
            <button
              type="button"
              className="flex items-start gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary transition-colors"
              onClick={() => documentInputRef.current?.click()}
              data-testid={`${testIdPrefix}-document-option`}
            >
              <svg
                className="w-4 h-4 mt-0.5 shrink-0 text-text-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {/* Two-line, unlike the media entries above: the document formats
                  are the least guessable of the four (five extensions, two of
                  them plain-text), and `documentHint` already existed for this
                  purpose but was never rendered. */}
              <span className="flex flex-col items-start leading-tight">
                <span>{t('uploadFlyout.uploadDocument')}</span>
                <span className="whitespace-nowrap text-xs font-medium text-text-muted">
                  {t('uploadFlyout.documentHint')}
                </span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  )
})

UploadFlyout.displayName = 'UploadFlyout'
export { UploadFlyout }
